import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import L from 'leaflet';

const SHADOW_COLOR = '#008fa3';

const asId = (value) => value?._id?.toString?.() || value?.toString?.() || '';

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const makeTooltip = (shadow) => {
  const areaName = escapeHtml(shadow.name || 'Unnamed area');
  const propertyName = escapeHtml(shadow.sourceJobsite?.name || shadow.sourceJobsite?.address || 'Nearby property');
  return `<strong>${areaName}</strong><br/>From ${propertyName}<br/><span style="color:#607d8b">Click to review ownership</span>`;
};

const buildLayer = (shadow) => {
  if (!Array.isArray(shadow.paths) || !shadow.paths.length) return null;
  const style = {
    color: SHADOW_COLOR,
    fillColor: SHADOW_COLOR,
    fillOpacity: 0.10,
    opacity: 0.9,
    weight: 3,
    dashArray: '9, 7',
  };

  if (shadow.geometryType === 'point') {
    const point = shadow.paths[0];
    if (!Number.isFinite(Number(point?.lat)) || !Number.isFinite(Number(point?.lng))) return null;
    return L.circleMarker([point.lat, point.lng], {
      ...style,
      radius: 7,
      fillOpacity: 0.22,
    });
  }
  if (shadow.geometryType === 'ellipse') {
    const point = shadow.paths[0];
    if (!Number.isFinite(Number(point?.lat)) || !Number.isFinite(Number(point?.lng))) return null;
    return L.circle([point.lat, point.lng], {
      ...style,
      radius: Number(shadow.radiusX || 0) / 3.28084,
    });
  }

  const latlngs = shadow.paths
    .filter(point => Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng)))
    .map(point => [point.lat, point.lng]);
  if (!latlngs.length) return null;
  if (shadow.geometryType === 'polyline') {
    return L.polyline(latlngs, { ...style, weight: 5 });
  }
  if (latlngs.length < 3) return null;
  return L.polygon(latlngs, style);
};

export default function useNearbyAreaShadows({
  url,
  jobsite,
  mapRef,
  visible = true,
  destinationTakeoffId,
  onAreasChanged,
  onToast,
}) {
  const [neighbors, setNeighbors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState('');
  const [selectedShadowId, setSelectedShadowId] = useState(null);
  const layerGroupRef = useRef(null);
  const layerByAreaIdRef = useRef({});

  const shadows = useMemo(() => (
    neighbors.flatMap(neighbor => (neighbor.areas || []).map(area => ({
      ...area,
      sourceJobsite: neighbor.jobsite,
      sourceTakeoff: neighbor.takeoff,
      distanceMiles: neighbor.distanceMiles,
    })))
  ), [neighbors]);

  const rootShadows = useMemo(() => shadows.filter(shadow => !shadow.parentAreaId), [shadows]);

  const selectedShadow = useMemo(() => {
    const selected = shadows.find(shadow => asId(shadow) === selectedShadowId);
    if (!selected?.parentAreaId) return selected || null;
    return shadows.find(shadow => asId(shadow) === asId(selected.parentAreaId)) || selected;
  }, [selectedShadowId, shadows]);

  const reload = useCallback(async () => {
    if (!url || !jobsite?._id) {
      setNeighbors([]);
      setSelectedShadowId(null);
      return [];
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${url}/api/customer/site-takeoff-neighbors/${jobsite._id}`
      );
      const nextNeighbors = response.data?.neighbors || [];
      setNeighbors(nextNeighbors);
      return nextNeighbors;
    } catch (requestError) {
      const message = requestError.response?.data?.message || 'Could not load nearby property areas';
      setError(message);
      setNeighbors([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [jobsite?._id, url]);

  useEffect(() => {
    setSelectedShadowId(null);
    reload();
  }, [reload]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    if (!layerGroupRef.current) layerGroupRef.current = L.featureGroup();
    const group = layerGroupRef.current;
    group.clearLayers();
    layerByAreaIdRef.current = {};

    shadows.forEach(shadow => {
      const layer = buildLayer(shadow);
      if (!layer) return;
      const shadowId = asId(shadow);
      layer.bindTooltip(makeTooltip(shadow), { sticky: true, direction: 'top' });
      layer.on('click', event => {
        if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
        setSelectedShadowId(shadowId);
      });
      group.addLayer(layer);
      layerByAreaIdRef.current[shadowId] = layer;
    });

    if (visible && shadows.length) {
      if (!map.hasLayer(group)) group.addTo(map);
      group.eachLayer(layer => layer.bringToBack?.());
    } else if (map.hasLayer(group)) {
      map.removeLayer(group);
    }

    return () => {
      group.clearLayers();
      layerByAreaIdRef.current = {};
    };
  }, [mapRef, shadows, visible]);

  useEffect(() => () => {
    const map = mapRef.current;
    if (map && layerGroupRef.current && map.hasLayer(layerGroupRef.current)) {
      map.removeLayer(layerGroupRef.current);
    }
  }, [mapRef]);

  const selectShadow = useCallback((shadow) => {
    setSelectedShadowId(shadow ? asId(shadow) : null);
  }, []);

  const focusShadow = useCallback((shadow) => {
    if (!shadow) return;
    const layer = layerByAreaIdRef.current[asId(shadow)];
    const map = mapRef.current;
    if (!layer || !map) return;
    try {
      if (layer.getBounds) map.fitBounds(layer.getBounds().pad(0.35), { animate: true });
      else if (layer.getLatLng) map.setView(layer.getLatLng(), Math.max(map.getZoom(), 19), { animate: true });
    } catch (_) {}
  }, [mapRef]);

  const moveAreaHere = useCallback(async (shadow) => {
    if (!url || !jobsite?._id || !shadow?._id) return null;
    setMoving(true);
    setError('');
    try {
      const response = await axios.post(
        `${url}/api/customer/site-takeoff-neighbors/${jobsite._id}/reassign`,
        {
          areaId: shadow._id,
          sourceTakeoffId: shadow.sourceTakeoff?._id,
          destinationTakeoffId: destinationTakeoffId || undefined,
        }
      );
      if (!response.data?.success) throw new Error(response.data?.message || 'Could not move area');
      await onAreasChanged?.(response.data.destinationTakeoffId);
      await reload();
      setSelectedShadowId(null);
      const count = response.data.movedAreaCount || 1;
      onToast?.(`${count === 1 ? 'Area' : `${count} related areas`} moved to this property`);
      return response.data;
    } catch (requestError) {
      const message = requestError.response?.data?.message || requestError.message || 'Could not move area';
      setError(message);
      return null;
    } finally {
      setMoving(false);
    }
  }, [destinationTakeoffId, jobsite?._id, onAreasChanged, onToast, reload, url]);

  return {
    neighbors,
    shadows,
    rootShadows,
    visible,
    loading,
    moving,
    error,
    selectedShadow,
    selectShadow,
    focusShadow,
    moveAreaHere,
    reload,
  };
}
